import { useEffect, useState } from "react";
import { Inbox, CalendarCheck, ListChecks, DollarSign, Clock, Check, Video, CreditCard, AlertTriangle, MailWarning, Mail, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth, hasPerm } from "@/context/AuthContext";
import { SectionTitle } from "../ui";
import { AdminPushAlerts } from "@/components/AdminPushAlerts";

const STATS = [
  { key: "contacts", label: "Contact messages", icon: Inbox },
  { key: "quizzes", label: "Quiz responses", icon: ListChecks },
  { key: "bookings", label: "Confirmed bookings", icon: CalendarCheck },
  { key: "pending", label: "Pending payment", icon: Clock },
];

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso.includes("Z") ? iso : iso + ":00");
  return d.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function Overview() {
  const { admin } = useAuth();
  const [stats, setStats] = useState(null);
  const [appts, setAppts] = useState(null); // null=loading, []=none, "denied"
  const [payStatus, setPayStatus] = useState(null);
  const [payments, setPayments] = useState([]);
  const [failedEmails, setFailedEmails] = useState(null);
  const nowStr = new Date().toISOString().slice(0, 16);
  const canViewPayments = hasPerm(admin, "settings");
  const canViewAppts = hasPerm(admin, "scheduler_view");

  const loadAppts = () =>
    api.get("/admin/appointments")
      .then((r) => setAppts(r.data))
      .catch((e) => setAppts(e?.response?.status === 403 ? "denied" : []));

  useEffect(() => {
    api.get("/admin/stats").then((res) => setStats(res.data)).catch(() => {});
    api.get("/admin/payments/status").then((r) => setPayStatus(r.data)).catch(() => {});
    api.get("/admin/failed-emails").then((r) => setFailedEmails(r.data)).catch(() => {});
    if (canViewPayments) api.get("/admin/payments").then((r) => setPayments(r.data)).catch(() => {});
    if (canViewAppts) loadAppts(); else setAppts("denied");
  }, [canViewPayments, canViewAppts]);

  const [digest, setDigest] = useState({ body: "", busy: false });

  const previewDigest = async () => {
    setDigest((d) => ({ ...d, busy: true }));
    try {
      const { data } = await api.get("/admin/digest/preview");
      setDigest({ body: data.body, busy: false });
    } catch { setDigest({ body: "", busy: false }); toast.error("Could not load the digest preview"); }
  };

  const sendDigest = async () => {
    setDigest((d) => ({ ...d, busy: true }));
    try {
      const { data } = await api.post("/admin/digest/send");
      if (data.sent) toast.success(`Digest sent — ${data.leads} leads, ${data.pet_transport_interest} pet-transport interest`);
      else toast.error(`Digest not sent: ${data.reason || "email unavailable"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the digest"); }
    finally { setDigest((d) => ({ ...d, busy: false })); }
  };

  const approve = async (id) => {
    try { await api.post(`/admin/appointments/${id}/approve`); toast.success("Appointment confirmed"); loadAppts(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not approve"); }
  };

  const pending = Array.isArray(appts) ? appts.filter((a) => a.status === "awaiting_approval") : [];
  const upcoming = Array.isArray(appts)
    ? appts.filter((a) => a.status === "confirmed" && (a.slot || "") >= nowStr).sort((a, b) => (a.slot > b.slot ? 1 : -1)).slice(0, 6)
    : [];

  return (
    <div data-testid="admin-overview">
      <SectionTitle title="Overview" subtitle="A calm snapshot of your business." />
      <div className="mb-6 -mt-2">
        <AdminPushAlerts />
      </div>

      {stats?.attention?.total > 0 && (
        <div data-testid="attention-banner" className="mb-6 rounded-xl border border-terracotta/40 bg-terracotta/5 px-5 py-4">
          <p className="text-sm font-medium text-terracotta">
            Needs attention · {stats.attention.total} item{stats.attention.total > 1 ? "s" : ""}
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            {stats.attention.unreplied_messages > 0 && (
              <span data-testid="attention-messages" className="rounded-full bg-white border border-sand-200 px-3 py-1 text-charcoal-700">
                {stats.attention.unreplied_messages} unreplied message{stats.attention.unreplied_messages > 1 ? "s" : ""} → Submissions
              </span>
            )}
            {stats.attention.bookings_awaiting > 0 && (
              <span data-testid="attention-bookings" className="rounded-full bg-white border border-sand-200 px-3 py-1 text-charcoal-700">
                {stats.attention.bookings_awaiting} booking{stats.attention.bookings_awaiting > 1 ? "s" : ""} awaiting approval → Appointments
              </span>
            )}
            {stats.attention.waiting_chats > 0 && (
              <span data-testid="attention-chats" className="rounded-full bg-white border border-sand-200 px-3 py-1 text-charcoal-700">
                {stats.attention.waiting_chats} chat{stats.attention.waiting_chats > 1 ? "s" : ""} waiting → Chat
              </span>
            )}
          </div>
        </div>
      )}

      {failedEmails?.count_7d > 0 && (
        <div data-testid="failed-emails-widget" className="mb-6 rounded-xl border border-red-300 bg-red-50 px-5 py-4">
          <p className="flex items-center gap-2 text-sm font-medium text-red-700">
            <MailWarning size={16} /> Email delivery issues · {failedEmails.count_24h} failed in the last 24h · {failedEmails.count_7d} in 7 days
          </p>
          <div className="mt-2 space-y-1">
            {failedEmails.recent.slice(0, 5).map((f) => (
              <p key={f.id} className="text-xs text-red-800/80 truncate">
                <span className="font-medium">{f.recipient || "(no recipient)"}</span> — "{f.subject}" · {f.reason}
              </p>
            ))}
          </div>
        </div>
      )}

      {payStatus && payStatus.mode !== "live" && (
        <div data-testid="stripe-mode-banner" className="mb-6 flex items-start gap-3 rounded-xl border border-clay-300 bg-clay-100/60 px-4 py-3">
          <AlertTriangle size={18} className="text-clay-800 shrink-0 mt-0.5" />
          <p className="text-sm text-clay-900">
            <b>Stripe is in {payStatus.mode === "test" ? "TEST mode" : "an unconfigured state"}.</b>{" "}
            {payStatus.mode === "test"
              ? "Payments use test cards only — no real money moves. Set your live secret key (sk_live_…) in the production environment before launch."
              : "No Stripe key found. Set STRIPE_API_KEY in the environment."}
          </p>
        </div>
      )}

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {STATS.map((s) => (
          <div key={s.key} className="rounded-xl bg-white p-6 border border-sand-200 shadow-sm">
            <s.icon className="text-sage-700" strokeWidth={1.5} />
            <p className="mt-4 font-serif text-3xl text-charcoal-800" data-testid={`stat-${s.key}`}>{stats ? stats[s.key] : "—"}</p>
            <p className="text-sm text-charcoal-500 mt-1">{s.label}</p>
          </div>
        ))}
        <div className="rounded-xl bg-charcoal-800 p-6 shadow-sm text-sand-100">
          <DollarSign className="text-sage" strokeWidth={1.5} />
          <p className="mt-4 font-serif text-3xl" data-testid="stat-revenue">${stats ? Number(stats.revenue).toLocaleString() : "—"}</p>
          <p className="text-sm text-sand-100/70 mt-1">Consultation revenue</p>
        </div>
      </div>

      {appts !== "denied" && (
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          {/* Pending approval */}
          <div data-testid="overview-pending" className="rounded-2xl bg-white p-6 border border-sand-200">
            <h3 className="font-serif text-xl text-charcoal-800 mb-4">Pending approval {pending.length > 0 && <span className="ml-1 rounded-full bg-clay-100 px-2 py-0.5 text-xs text-clay-800">{pending.length}</span>}</h3>
            {pending.length === 0 && <p className="text-sm text-charcoal-500">Nothing waiting — you're all caught up. 🎉</p>}
            <div className="space-y-3">
              {pending.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-3 rounded-xl bg-sand-100 px-4 py-3">
                  <div className="min-w-0">
                    <p className="font-medium text-charcoal-800 truncate">{a.name} · {a.type_name}</p>
                    <p className="text-xs text-charcoal-500">{fmt(a.slot)}{a.deposit_amount > 0 ? ` · $${a.deposit_amount} ${a.deposit_paid ? "paid" : "unpaid"}` : ""}</p>
                  </div>
                  <button data-testid={`overview-approve-${a.id}`} onClick={() => approve(a.id)} className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-sage px-4 py-1.5 text-sm text-white hover:bg-sage-hover"><Check size={14} /> Approve</button>
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming */}
          <div data-testid="overview-upcoming" className="rounded-2xl bg-white p-6 border border-sand-200">
            <h3 className="font-serif text-xl text-charcoal-800 mb-4">Upcoming appointments</h3>
            {upcoming.length === 0 && <p className="text-sm text-charcoal-500">No upcoming appointments yet.</p>}
            <div className="space-y-3">
              {upcoming.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-3 rounded-xl bg-sand-100 px-4 py-3">
                  <div className="min-w-0">
                    <p className="font-medium text-charcoal-800 truncate">{a.name} · {a.type_name}</p>
                    <p className="text-xs text-charcoal-500">{fmt(a.slot)} · {a.duration_minutes}min</p>
                  </div>
                  {a.manage_token && (
                    <a data-testid={`overview-join-${a.id}`} href={`/call?token=${a.manage_token}`} target="_blank" rel="noreferrer" className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-terracotta px-4 py-1.5 text-sm text-white hover:bg-terracotta-hover"><Video size={14} /> Join</a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {canViewPayments && (
      <div data-testid="overview-payments" className="mt-10 rounded-2xl bg-white p-6 border border-sand-200">
        <h3 className="flex items-center gap-2 font-serif text-xl text-charcoal-800 mb-4">
          <CreditCard size={18} className="text-terracotta" /> Recent payments
          {payStatus && (
            <span className={`ml-2 rounded-full px-2.5 py-0.5 text-[11px] uppercase tracking-wide ${payStatus.mode === "live" ? "bg-sage/15 text-sage-700" : "bg-clay-100 text-clay-800"}`}>
              {payStatus.mode} mode
            </span>
          )}
        </h3>
        {payments.length === 0 ? (
          <p className="text-sm text-charcoal-500">No payment activity yet.</p>
        ) : (
          <div className="divide-y divide-sand-100">
            {payments.slice(0, 10).map((p) => (
              <div key={p.session_id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                <div className="min-w-0">
                  <span className="font-medium text-charcoal-800">{p.email || "—"}</span>
                  <span className="ml-2 text-xs text-charcoal-400 capitalize">{(p.type || "").replace("_", " ")}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-charcoal-700">${Number(p.amount || 0).toLocaleString()}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] capitalize ${p.payment_status === "paid" ? "bg-sage/15 text-sage-700" : p.payment_status === "initiated" ? "bg-sand-200 text-charcoal-600" : "bg-clay-100 text-clay-800"}`}>
                    {p.payment_status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {canViewPayments && (
        <div data-testid="digest-widget" className="mt-10 rounded-2xl bg-white p-6 border border-sand-200">
          <h3 className="flex items-center gap-2 font-serif text-xl text-charcoal-800 mb-1">
            <Mail size={18} className="text-terracotta" /> Weekly digest
          </h3>
          <p className="text-sm text-charcoal-500 mb-4">
            Every Monday we email you a summary of new leads, pet-transport interest, bookings and pet stories. Preview it or send one now.
          </p>
          <div className="flex flex-wrap gap-3">
            <button data-testid="digest-preview-btn" onClick={previewDigest} disabled={digest.busy}
              className="inline-flex items-center gap-1.5 rounded-full border border-charcoal-300 px-5 py-2 text-sm font-medium text-charcoal-800 hover:bg-sand-200 disabled:opacity-50">
              <Eye size={14} /> Preview
            </button>
            <button data-testid="digest-send-btn" onClick={sendDigest} disabled={digest.busy}
              className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-5 py-2 text-sm font-medium text-white hover:bg-terracotta-hover disabled:opacity-50">
              <Mail size={14} /> Send now
            </button>
          </div>
          {digest.body && (
            <pre data-testid="digest-preview-body" className="mt-4 whitespace-pre-wrap rounded-xl bg-sand-100 px-5 py-4 text-sm leading-relaxed text-charcoal-700 font-sans">
              {digest.body}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
